import { GOODFLIP_VITALS, GOODFLIP_ACHIEVE } from "../../envConstants";
import backIcon from "../../assets/images/arrow-left.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useMemo, memo, useEffect, useCallback, useRef } from "react";
import { getInitials } from "../../utils/helper";
import { PAID, PENDING, PLAN_STATUS_MAP } from "../../utils/constants";
import { getDecodedToken } from "../../utils/localStorage";
import trophy from "../../assets/images/trophy.svg";
import heart from "../../assets/images/heart-icon.svg";
import { Conditions, CarePlanDiseaseTypes } from "../../types";
import NoMetabolicScore from "./NoMetabolicScore";

type TabType = "medeco-summary" | "vitals";

enum ConditionsEnums {
  DIABETES = "diabetes",
  PCOS = "pcos",
  FATTYLIVER = "fatty-liver",
  FATTYLIVER_NASH = "fatty liver - nash",
  OBESITY = "obesity",
  KIDNEY = "kidney",
  OTHERS = "others",
}

interface PatientData {
  name: string;
  contact_no?: string;
  mobile?: string;
  medical_condition_name?: string;
  therapyArea?: string;
  patient_id?: string;
  status?: string;
  fromPath?: string;
  referral_status?: string;
}

const Header = memo(
  ({
    patient,
    activeTab,
    onTabChange,
    onBack,
    status,
  }: {
    patient: PatientData;
    activeTab: TabType;
    onTabChange: (tab: TabType) => void;
    onBack: () => void;
    status: string;
  }) => {
    const mappedStatus =
      PLAN_STATUS_MAP[status as keyof typeof PLAN_STATUS_MAP];

    return (
      <header className="w-full max-w-[1280] mx-auto bg-white z-10">
        <div className="flex gap-1.5 items-center px-4 py-4 text-base font-semibold tracking-tight text-center bg-white border-b border-solid border-b-zinc-200 text-zinc-900">
          <img
            loading="lazy"
            src={backIcon}
            alt="Back"
            className="object-contain shrink-0 self-stretch my-auto w-6 aspect-square cursor-pointer"
            onClick={onBack}
          />
          <div className="flex gap-3 justify-center items-start w-full">
            <div className="flex flex-1 shrink gap-3 items-center basis-0 min-w-[240px] px-3">
              <div className="overflow-hidden self-stretch my-auto w-12 h-12 text-lg font-semibold leading-none text-purple-500 whitespace-nowrap bg-violet-50 min-h-[48px] rounded-[80px] flex items-center justify-center">
                {patient.name ? getInitials(patient.name) : ""}
              </div>
              <div className="flex flex-col flex-1 shrink self-stretch my-auto basis-0 text-left">
                <h3 className="text-base font-semibold text-gray-800">
                  {patient.name}
                </h3>
                <p className="mt-1 text-sm leading-loose text-gray-700 font-normal">
                  {patient.medical_condition_name}
                </p>
              </div>
              {mappedStatus && (
                <div
                  className={`overflow-hidden gap-2 self-stretch px-3 py-1 my-auto text-xs font-medium ${
                    status === PENDING
                      ? "text-orange-400 bg-orange-100"
                      : status === PAID
                      ? "text-[#1F8B24] bg-[#C5F2C7]"
                      : "text-[#DA100B] bg-[#FCD0CF]"
                  } rounded-3xl`}
                >
                  {mappedStatus}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="shadow-md bg-white">
          <div className="flex gap-8 justify-evenly">
            <TabButton
              isActive={activeTab === "vitals"}
              onClick={() => onTabChange("vitals")}
              icon={heart}
              label="Vitals"
            />
            <TabButton
              isActive={activeTab === "medeco-summary"}
              onClick={() => onTabChange("medeco-summary")}
              icon={trophy}
              label="Summary"
            />
          </div>
        </div>
      </header>
    );
  }
);

const TabButton = memo(
  ({
    isActive,
    onClick,
    icon,
    label,
  }: {
    isActive: boolean;
    onClick: () => void;
    icon: string;
    label: string;
  }) => (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-2 pt-3 pb-1 text-sm font-medium rounded-t-lg ${isActive ? "text-indigo-600" : ""
        }`}
    >
      <span
        className={`flex items-center font-roboto text-base px-6 py-2 leading-6 tracking-[0.25px] ${isActive
            ? "bg-indigo-50 text-indigo-600 rounded-3xl mb-1 font-bold"
            : ""
          }`}
      >
        {isActive && (
          <img src={icon} alt={label} className="w-[16px] h-[16px] mr-1" />
        )}
        {label}
      </span>
      {isActive && (
        <span className="absolute bottom-0 left-1/2 w-[66px] h-[5px] bg-indigo-600 rounded-t-lg -translate-x-1/2"></span>
      )}
    </button>
  )
);

// Loading spinner component
const LoadingSpinner = memo(() => (
  <div className="flex items-center justify-center h-full">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  </div>
));

// Error display component
const ErrorDisplay = memo(({ onRetry }: { onRetry: () => void }) => (
  <div className="flex items-center justify-center h-full">
    <div className="flex flex-col items-center gap-4 p-6 text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
        <svg
          className="w-8 h-8 text-red-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Failed to load content
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          There was an error loading the content. Please try again.
        </p>
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  </div>
));

const IframeView = memo(
  ({
    url,
    title,
    className = "",
  }: {
    url: string;
    title: string;
    className?: string;
  }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();
    const loadedRef = useRef(false);

    const handleLoad = useCallback(() => {
      loadedRef.current = true;
      setIsLoading(false);
      setHasError(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }, []);

    const handleError = useCallback(() => {
      setIsLoading(false);
      setHasError(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }, []);

    const handleRetry = useCallback(() => {
      setRetryCount((prev) => prev + 1);
      setIsLoading(true);
      setHasError(false);
      loadedRef.current = false;
      if (iframeRef.current) {
        iframeRef.current.src = url;
      }
    }, [url]);

    // Reset loading state when URL changes
    useEffect(() => {
      setIsLoading(true);
      setHasError(false);
      loadedRef.current = false;

      // Set a timeout for loading - only if not already loaded
      timeoutRef.current = setTimeout(() => {
        if (!loadedRef.current) {
          setIsLoading(false);
          setHasError(true);
        }
      }, 30000); // 30 second timeout

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [url, retryCount]); // Removed isLoading from dependencies to prevent infinite loop

    if (hasError) {
      return <ErrorDisplay onRetry={handleRetry} />;
    }

    return (
      <div className="relative w-full h-full">
        {isLoading && (
          <div className="absolute inset-0 z-10 bg-white">
            <LoadingSpinner />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={url}
          title={title}
          className={`w-full h-full border-none ${className}`}
          allow="accelerometer; autoplay; camera; clipboard-read; clipboard-write; encrypted-media; fullscreen; geolocation; gyroscope; microphone; picture-in-picture"
          allowFullScreen
          width="100%"
          height="100%"
          onLoad={handleLoad}
          onError={handleError}
          loading="eager"
          style={{
            visibility: isLoading ? "hidden" : "visible",
          }}
        />
      </div>
    );
  }
);

export default function Goodflip() {
  const navigate = useNavigate();
  const { state } = useLocation() as { state: PatientData };
  const tokenData: any = getDecodedToken();
  const doctorNumber = tokenData?.user?.mobileNumber || "";
  const doctorB2cId = tokenData?.user?.b2cId || "";

  const [activeTab, setActiveTab] = useState<TabType>("vitals");
  const [showHeader, setShowHeader] = useState(true); // Default to showing header

  const status = state?.referral_status || state?.status || "";

  useEffect(() => {
    const handleMessage = (event: any) => {
      if (event.data && event.data.type === "ROUTE_CHANGE") {
        // Show header only if it's the base page
        setShowHeader(
          event.data.payload?.path.endsWith("/medeco-summary") ||
            event.data.payload?.path.endsWith("/score/goals") ||
            event.data.payload?.path.endsWith("/score/result") ||
            event.data.payload?.path.endsWith("/progress/vitals") ||
            event.data.payload?.path === "/"
        );
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [navigate]);

  const conditionNameToDiseaseMapping = useCallback(
    (condition_name: CarePlanDiseaseTypes): Conditions => {
      switch (condition_name) {
        case "Diabetes":
          return ConditionsEnums.DIABETES;
        case "PCOS":
          return ConditionsEnums.PCOS;
        case "Fatty Liver - NASH":
        case "Fatty Liver - NAFL":
          return ConditionsEnums.FATTYLIVER;
        case "Obesity":
          return ConditionsEnums.OBESITY;
        case "Chronic Kidney Disease":
          return ConditionsEnums.KIDNEY;
        default:
          return ConditionsEnums.OTHERS;
      }
    },
    []
  );

  const medicalConditionName = useMemo(() => {
    return conditionNameToDiseaseMapping(
      state?.medical_condition_name as CarePlanDiseaseTypes
    );
  }, [state?.medical_condition_name, conditionNameToDiseaseMapping]);

  // Memoize the data preparation to avoid unnecessary recalculations
  const { achieveUrl, vitalsUrl } = useMemo(() => {
    const data = {
      mobileNumber: state?.contact_no || state?.mobile,
      name: state?.name,
      doctorNumber,
      doctorB2cId,
      clientSource: "medeco",
      moduleName:
        activeTab === "medeco-summary" ? "medeco-summary" : "progress",
      environment: process.env.REACT_APP_ENV,
      ...(activeTab === "medeco-summary"
        ? {
          conditionName: state?.medical_condition_name?.toLowerCase(),
          therapyArea: state?.therapyArea?.toLowerCase(),
          patientID: state?.patient_id,
        }
        : {
          medical_condition_name: state?.medical_condition_name,
        }),
    };

    const jsonString = JSON.stringify(data);
    const encodedData = encodeURIComponent(jsonString);

    return {
      achieveUrl: `${GOODFLIP_ACHIEVE}/${state?.therapyArea?.toLowerCase()}/${medicalConditionName}/medeco-summary/?data=${encodedData}`,
      vitalsUrl: `${GOODFLIP_VITALS}/?data=${encodedData}`,
    };
  }, [
    state?.contact_no,
    state?.mobile,
    state?.name,
    doctorNumber,
    doctorB2cId,
    state?.medical_condition_name,
    state?.therapyArea,
    state?.patient_id,
    activeTab,
    medicalConditionName,
  ]);

  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
  }, []);

  const handleBack = () => {
    navigate(state?.fromPath || "/", { state });
  };

  return (
    <div className="bg-white flex flex-col h-screen overflow-hidden">
      {showHeader && (
        <Header
          patient={state}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onBack={handleBack}
          status={status}
        />
      )}
      {activeTab === "medeco-summary" &&
      (!state?.therapyArea || !state?.medical_condition_name) ? (
        <NoMetabolicScore />
      ) : (
        <main className="flex-grow w-full relative">
          {/* Single iframe view with active URL */}
          <IframeView
            url={activeTab === "vitals" ? vitalsUrl : achieveUrl}
            title={activeTab === "vitals" ? "Vitals" : "Summary"}
            className="bg-[#F9F9FF]"
          />
        </main>
      )}
    </div>
  );
}
